import { Fragment, useState } from 'react';

import type { CrawlRun } from '@/hooks/useCrawlRuns';
import type { CrawlBatch } from '@/lib/crawlRuns';
import { relTime } from '@/lib/crawlRuns';

import { CheckCircleIcon, ChevronDownIcon, CronIcon } from './crawlIcons';
import { CrawlRunRow } from './CrawlRunRow';

const GREEN = '#3f7d4e';

function Divider() {
  return <div className="h-px bg-wolisho-brown/[0.06]" />;
}

function BatchGroup({
  label,
  color,
  children,
}: {
  label: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <div className="py-2.5">
      <p className="mb-0.5 text-[10.5px] font-bold uppercase tracking-[0.05em]" style={{ color }}>
        {label}
      </p>
      {children}
    </div>
  );
}

interface CrawlBatchRowProps {
  batch: CrawlBatch;
  defaultOpen?: boolean;
  onRerun: (run: CrawlRun) => void;
}

// A collapsible cron pass: failures and warnings surface first; the clean tail
// is summarized behind a toggle so a batch scales to hundreds of roasters.
export function CrawlBatchRow({ batch, defaultOpen, onRerun }: CrawlBatchRowProps) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const [showClean, setShowClean] = useState(false);
  const cleanCount = batch.okCount - batch.warnCount;

  return (
    <div className="px-4 py-[13px]" data-testid={`crawl-batch-${batch.batchId}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2.5 border-0 bg-transparent p-0 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[14.5px] font-bold text-black">{batch.label}</span>
            <span className="inline-flex items-center gap-1 rounded-md border border-wolisho-brown/[0.14] px-[7px] py-0.5 text-[10.5px] font-bold uppercase tracking-[0.03em] text-wolisho-brown/40">
              <CronIcon size={10} /> Cron
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-[7px] text-xs font-bold">
            <span className="text-black/50">{batch.roasterCount} roasters</span>
            <span className="text-black/[0.18]">·</span>
            <span style={{ color: GREEN }}>{batch.okCount} ok</span>
            {batch.failedCount > 0 && (
              <>
                <span className="text-black/[0.18]">·</span>
                <span className="text-wolisho-red">{batch.failedCount} failed</span>
              </>
            )}
            {batch.warnCount > 0 && (
              <>
                <span className="text-black/[0.18]">·</span>
                <span className="text-wolisho-brown">{batch.warnCount} warned</span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11.5px] font-bold tabular-nums text-black/40">{relTime(batch.startedAt)}</span>
          <span className={`flex text-wolisho-brown/35 transition-transform ${open ? 'rotate-180' : ''}`}>
            <ChevronDownIcon size={16} />
          </span>
        </div>
      </button>

      {open && (
        <div className="mt-3 rounded-lg bg-wolisho-cream px-3 py-0.5">
          {batch.failedCount > 0 && (
            <BatchGroup label={`Failed · ${String(batch.failedCount)}`} color="var(--wolisho-red, #CF213E)">
              {batch.failed.map((run, index) => (
                <Fragment key={run.id}>
                  {index > 0 && <Divider />}
                  <CrawlRunRow run={run} onRerun={onRerun} defaultOpen={index === 0} />
                </Fragment>
              ))}
            </BatchGroup>
          )}
          {batch.warnCount > 0 && (
            <BatchGroup label={`With warnings · ${String(batch.warnCount)}`} color="var(--wolisho-brown, #361100)">
              {batch.warned.map((run, index) => (
                <Fragment key={run.id}>
                  {index > 0 && <Divider />}
                  <CrawlRunRow run={run} onRerun={onRerun} />
                </Fragment>
              ))}
            </BatchGroup>
          )}
          {cleanCount > 0 && (
            <div className="px-0.5 py-[11px]">
              {batch.okClean.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowClean((value) => !value)}
                    className="inline-flex items-center gap-1.5 border-0 bg-transparent p-0 text-[12.5px] font-bold text-wolisho-brown"
                  >
                    <span style={{ color: GREEN }}>
                      <CheckCircleIcon size={13} />
                    </span>
                    {showClean ? 'Hide' : 'Show'} {cleanCount} that completed cleanly
                  </button>
                  {showClean && (
                    <div className="mt-1">
                      {batch.okClean.map((run, index) => (
                        <Fragment key={run.id}>
                          {index > 0 && <Divider />}
                          <CrawlRunRow run={run} onRerun={onRerun} />
                        </Fragment>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-wolisho-brown/50">
                  <span style={{ color: GREEN }}>
                    <CheckCircleIcon size={13} />
                  </span>
                  + {cleanCount} completed cleanly
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
